import { useEffect, useRef, useState, useMemo } from "react";
import { Box } from "../../primitives/box";
import { Text } from "../../primitives/text";
import { Icon } from "../icon/index";
import { Button } from "../button";
import { useHeaderStyles } from "./styles";
import type { HeaderProps } from "./types";
import { useTheme } from "@repo/tokens";
import type { IconName } from "../icon/iconsMap";
import {
  userAccountPhotoShouldUseInitials,
  resolveHeaderProfileOrIconName,
} from "../icon/profileAvatarIcon";
import { Modal } from "../modal";
import { MenuDrawer } from "../MenuDrawer";
import { useIsMobile, useIsTablet } from "@repo/utils";
import { useHeaderAvatarActions } from "@repo/tokens";
import type { HeaderAvatarActionId } from "@repo/tokens";
import {
  getInitialsFromName,
  shouldShowHeaderAvatarMenuItem,
} from "@repo/utils";
import { resolveUserAccountPhotoToIconName } from "@repo/ui";

export const Header = ({
  title,
  subtitle,
  breadcrumb,
  left,
  backIcon,
  backIconShadow = false,
  backIconWidth = 48,
  right,
  center,
  onMenuPress,
  onBackPress,
  onRightPress,
  rightIconItems,
  showLeft = true,
  showCenter = true,
  showRight = true,
  accessibilityLabel,
  padding,
  titleColor,
  titleAlign = "center",
  titleSize: _titleSize = 16,
  headerHeight,
  containerStyles,
  sectionLeftStyles,
  titleStyles,
  navigate,
  handleLogOut,
  titleContainerStyles,
}: HeaderProps) => {
  const headerAvatarCtx = useHeaderAvatarActions();
  const { theme } = useTheme();
  const isMobile = useIsMobile(1024);
  const isTablet = useIsTablet();
  const styles = useHeaderStyles({ padding, titleColor, headerHeight });
  /** True center when only left chrome + title (no right slot); avoids space-between shifting the title. */
  const useBalancedTitleRow =
    showRight === false &&
    titleAlign === "center" &&
    showCenter &&
    (Boolean(title) || Boolean(center)) &&
    showLeft &&
    !left &&
    !breadcrumb &&
    Boolean(onBackPress || onMenuPress);
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const rightPopoverWrapperRef = useRef<HTMLDivElement | null>(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    profilePhotoRaw: null,
  });

  const openDrawer = () => {
    if (!isMobile && !isTablet) {
      return;
    }
    setDrawerMounted(true);
    requestAnimationFrame(() => {
      setDrawerOpen(true);
    });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    window.setTimeout(() => {
      setDrawerMounted(false);
    }, 250);
  };

  const handleRightPanelOpenClose = () => {
    setRightPanelOpen(!rightPanelOpen);
  };

  useEffect(() => {
    if (!rightPanelOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const wrapper = rightPopoverWrapperRef.current;
      if (!wrapper) return;
      const target = event.target as Node | null;
      if (target && !wrapper.contains(target)) {
        setRightPanelOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setRightPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [rightPanelOpen]);

  const renderLeftContent = () => {
    if (breadcrumb && !isMobile)
      return <div style={styles.breadcrumStyle}>{breadcrumb}</div>;
    if (left) return left;

    if (onBackPress) {
      return (
        <Button
          style={styles.headerButton}
          variant="ghost"
          size="lg"
          onPress={onBackPress}
          leftIcon={
            <span style={backIconShadow ? styles.iconShadow : undefined}>
              <Icon
                name={(backIcon ?? "arrowBackIcon") as IconName}
                size={backIconWidth}
                color={theme.text.primary}
              />
            </span>
          }
        />
      );
    }

    if (onMenuPress) {
      return (
        <Button
          style={styles.headerButton}
          variant="ghost"
          size="lg"
          onPress={openDrawer}
          leftIcon={
            <span style={backIconShadow ? styles.iconShadow : undefined}>
              <Icon
                name={(backIcon ?? "headerMenu") as IconName}
                size={backIconWidth}
                color={theme.text.primary}
              />
            </span>
          }
        />
      );
    }

    return null;
  };

  const renderRightIconClickContent = () => {
    if (!rightIconItems) return null;

    // const filteredItems = rightIconItems.filter((item) => {
    //   if (
    //     headerAvatarCtx?.loginMethod === "phone_otp" ||
    //     headerAvatarCtx?.loginMethod === "admission_id"
    //   )
    //     return true;
    //   // Hide switch profile/product if not logged in via phone_otp
    //   return (
    //     item.id !== AVATAR_ACTION_ID.SWITCH_PROFILE &&
    //     item.id !== AVATAR_ACTION_ID.SWITCH_PRODUCT
    //   );
    // });
    const filteredItems = rightIconItems.filter((item) =>
      shouldShowHeaderAvatarMenuItem(
        item.id,
        headerAvatarCtx?.loginMethod,
        headerAvatarCtx?.isdCode,
        headerAvatarCtx?.fromGeniusDeeplink,
      ),
    );

    if (filteredItems.length === 0) return null;

    return (
      <Box style={styles.rightPopoverList} onClick={(e) => e.stopPropagation()}>
        {filteredItems.map((item) => (
          <Box
            key={item.id}
            style={styles.rightPopoverItem}
            onClick={() => {
              if (headerAvatarCtx) {
                headerAvatarCtx.openAction(item.id as HeaderAvatarActionId);
              } else {
                item.onPress?.();
              }
              handleRightPanelOpenClose();
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
          </Box>
        ))}
      </Box>
    );
  };

  const renderRightContent = () => {
    if (right) return right;

    const rightIconClickContent = renderRightIconClickContent();
    const trimmedInitials = headerRightAvatarInitials?.trim() ?? "";
    const trimmedRight =
      typeof headerRightIcon === "string" ? headerRightIcon.trim() : "";
    const hasProfilePhotoIcon =
      trimmedRight !== "" &&
      !userAccountPhotoShouldUseInitials(headerRightIcon) &&
      trimmedRight.toLowerCase() !== "null" &&
      trimmedRight.toLowerCase() !== "undefined";
    const showAvatar = hasProfilePhotoIcon || trimmedInitials.length > 0;

    if (!(showAvatar && (onRightPress || rightIconClickContent))) return null;

    return (
      <Box
        ref={rightPopoverWrapperRef}
        style={styles.rightPopoverWrapper}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          style={styles.headerButton}
          variant="ghost"
          size="sm"
          onClick={() => handleRightPanelOpenClose()}
          leftIcon={
            <div style={styles.rightIconWrapper}>
              <span style={styles.iconShadow}>
                <span
                  style={
                    hasProfilePhotoIcon
                      ? styles.rightAvatarCircle
                      : styles.rightAvatarInitialsContainer
                  }
                >
                  {hasProfilePhotoIcon ? (
                    <Icon
                      name={resolveHeaderProfileOrIconName(trimmedRight)}
                      size={40}
                      color={theme.action.primary.default}
                    />
                  ) : (
                    <span style={styles.rightAvatarInitialsText}>
                      {trimmedInitials}
                    </span>
                  )}
                </span>
              </span>
              {!isMobile && (
                <span
                  style={{
                    ...styles.rightArrow,
                    transform: rightPanelOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                >
                  <Icon
                    name="arrowDownNew"
                    size={24}
                    color={theme.colors.primary[40]}
                  />
                </span>
              )}
            </div>
          }
        />

        {rightPanelOpen && rightIconClickContent && (
          <Box style={styles.rightPopover} onClick={(e) => e.stopPropagation()}>
            {rightIconClickContent}
          </Box>
        )}
      </Box>
    );
  };

  const renderCenterContent = () => {
    if (!showCenter) return null;
    if (center) return center;

    return (
      <Box
        style={{
          ...styles.center,
          ...(useBalancedTitleRow && {
            minWidth: 0,
            width: "100%",
          }),
          ...(titleAlign !== "center" && {
            alignItems: titleAlign as any,
          }),
          ...titleContainerStyles,
        }}
      >
        {title && (
          <Text
            style={{
              ...styles.title,
              ...(titleAlign !== "center" && {
                textAlign: titleAlign as any,
              }),
              ...titleStyles,
            }}
          >
            {title}
          </Text>
        )}
        {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
      </Box>
    );
  };

  useEffect(() => {
    const localStoragedata = localStorage.getItem("auth-storage");

    if (localStoragedata) {
      try {
        const parsedData = JSON.parse(localStoragedata);

        setUserData({
          firstName:
            parsedData?.state?.profilesData?.unified_account_details
              ?.user_account_details?.first_name || "",
          lastName:
            parsedData?.state?.profilesData?.unified_account_details
              ?.user_account_details?.last_name || "",
          profilePhotoRaw:
            parsedData?.state?.profilesData?.unified_account_details
              ?.user_account_details?.profile_photo || null,
        });
      } catch (e) {
        console.error("Invalid JSON in localStorage", e);
      }
    }
  }, []);

  const { headerRightIcon, headerRightAvatarInitials } = useMemo(() => {
    const displayName = [userData.firstName.trim(), userData.lastName.trim()]
      .filter(Boolean)
      .join(" ");
    const initials = getInitialsFromName(displayName) || "0";
    if (userAccountPhotoShouldUseInitials(userData.profilePhotoRaw)) {
      return {
        headerRightIcon: undefined,
        headerRightAvatarInitials: initials,
      };
    }
    return {
      headerRightIcon: resolveUserAccountPhotoToIconName(
        userData.profilePhotoRaw,
      ),
      headerRightAvatarInitials: undefined,
    };
  }, [userData.profilePhotoRaw, userData.firstName, userData.lastName]);

  return (
    <>
      <Box
        as="header"
        aria-label={accessibilityLabel}
        style={{
          ...styles.container,
          ...(useBalancedTitleRow
            ? {
                display: "grid",
                gridTemplateColumns: `${backIconWidth / 16}rem minmax(0, 1fr) ${backIconWidth / 16}rem`,
                alignItems: "center",
                justifyContent: "normal",
              }
            : {}),
          ...containerStyles,
        }}
      >
        {showLeft && (
          <Box style={{ ...styles.sectionLeft, ...sectionLeftStyles }}>
            {renderLeftContent()}
          </Box>
        )}
        {renderCenterContent()}
        <Box style={styles.sectionRight}>
          {showRight && renderRightContent()}
        </Box>
      </Box>

      <Modal
        isModalOpen={drawerMounted && (isMobile || isTablet)}
        onClose={closeDrawer}
        showHeader={false}
        showFooter={false}
        width="100%"
        height="auto"
        overLayColor="transparent"
        styles={{
          overlay: styles.drawerModalOverlay,
          modal: {
            ...styles.drawerModal,
            ...(drawerOpen ? styles.drawerModalOpen : styles.drawerModalClosed),
          },
          body: styles.drawerModalBody,
        }}
        body={
          <MenuDrawer
            isOpen={drawerMounted}
            onClose={closeDrawer}
            navigate={navigate}
            handleLogOut={handleLogOut}
          />
        }
      />
    </>
  );
};

Header.displayName = "Header";
